import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUser } from "../../../../store/authSlice";
import { appApi, useGetNcsCustomersQuery, useGetNcsTeamQuery } from "../../../../store/appApi";
import {
  usePartnerCode,
  useProjectPartners,
  panelClass,
  fieldClass,
  btnGhost,
  Badge,
  STATUS_TONE,
  CUSTOMER_BUCKETS,
  fmtDate,
  fmtDateInput,
} from "./config";
import CustomerDrawer from "./CustomerDrawer";
import {
  downloadWorkbook,
  recoverFromTerminatedApi,
  transferToModemRecoveryApi,
  bulkTransferToModemRecoveryApi,
  cancelModemTransferApi,
  deleteCustomerApi,
} from "../../../../services/netplusCustomerStatus";
import { uiPrompt, uiToast, uiConfirm } from "./ui";

const NcsCustomers = () => {
  const dispatch = useDispatch();
  const isSuperAdmin = useSelector(selectUser)?.role === "super-admin";
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const { codes: allPartnerCodes } = useProjectPartners();
  // Local to this screen only — never written to the shared, cross-page
  // selected partner (usePartnerCode/localStorage), so switching to it here
  // doesn't affect Followups/Upload/Modem Recovery/etc.
  const [viewAll, setViewAll] = useState(false);
  const [bucket, setBucket] = useState("");
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  // Terminated-only bulk selection — checkboxes only apply to Terminated
  // rows, since that's the only status this bulk action targets. Not offered
  // while viewing "All partners" — a bulk transfer takes one partner code
  // for the whole batch.
  const [selectedForTransfer, setSelectedForTransfer] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const { data, isFetching } = useGetNcsCustomersQuery({
    partnerCode: viewAll ? "ALL" : partnerCode,
    bucket,
    q: qLive,
    from: fromDate,
    to: toDate,
    dateField: "activationDate",
    page,
    limit: 50,
  });

  const { data: teamData } = useGetNcsTeamQuery();
  const staff = (teamData?.members || [])
    .filter((m) => m.active !== false)
    .map((m) => ({ _id: m.userId?._id || m.userId, name: m.userId?.name || m.name }));

  const rows = data?.customers || [];
  const totalPages = data?.totalPages || 1;
  // Modem recovery applies to Terminated customers and to Active ones (e.g. a
  // customer who is leaving / shifting but still shows Active on the sheet).
  const canRecoverModem = (c) => c.status === "Terminated" || c.status === "Active";
  const terminatedKeys = rows.filter(canRecoverModem).map((c) => c.customerKey);
  const allTerminatedSelected =
    terminatedKeys.length > 0 &&
    terminatedKeys.every((k) => selectedForTransfer.has(k));
  const toggleSelectAllTerminated = () =>
    setSelectedForTransfer(
      allTerminatedSelected ? new Set() : new Set(terminatedKeys),
    );

  // Terminated subscriber whose physical modem was recovered — record it with
  // the recovered modem's MAC id ("acid").
  const recoverModem = async (c) => {
    const res = await uiPrompt({
      title: `Modem recovered — ${c.name || c.userId}`,
      confirmText: "Record",
      fields: [
        {
          name: "macId",
          label: "Recovered modem MAC id",
          type: "text",
          initial: c.macId || "",
          required: true,
          placeholder: "MAC / ACID",
        },
        {
          name: "note",
          label: "Remark (optional)",
          type: "textarea",
          placeholder: "condition, who collected, etc.",
        },
        // Super-admin only — everyone else records this to right now.
        ...(isSuperAdmin
          ? [
              {
                name: "transferredOn",
                label: "Recovered on (back-date if needed)",
                type: "date",
                initial: fmtDateInput(),
                max: fmtDateInput(),
              },
            ]
          : []),
      ],
    });
    if (res === null) return;
    try {
      await recoverFromTerminatedApi({
        partnerCode: c.partnerCode || partnerCode,
        customerKey: c.customerKey,
        macId: res.macId,
        note: res.note || "",
        transferredOn: res.transferredOn || undefined,
      });
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      uiToast({ type: "success", title: "Recovered modem recorded" });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Couldn't record",
        message: e?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  // Terminated subscriber — open a live Modem Recovery case for the recovery
  // team to chase (one at a time), same endpoint the Followups board uses.
  const transferToRecovery = async (c) => {
    const res = await uiPrompt({
      title: `Transfer to Modem Recovery — ${c.name || c.userId}`,
      confirmText: "Transfer",
      fields: [
        {
          name: "assignedTo",
          label: "Assign to",
          type: "select",
          options: [
            { value: "", label: "Unassigned" },
            ...staff.map((s) => ({ value: s._id, label: s.name })),
          ],
        },
        {
          name: "reason",
          label: "Reason (optional)",
          type: "textarea",
          placeholder: "e.g. Terminated / customer leaving — recover the modem",
        },
        ...(isSuperAdmin
          ? [
              {
                name: "transferredOn",
                label: "Transferred on (back-date if needed)",
                type: "date",
                initial: fmtDateInput(),
                max: fmtDateInput(),
              },
            ]
          : []),
      ],
    });
    if (res === null) return;
    try {
      await transferToModemRecoveryApi({
        partnerCode: c.partnerCode || partnerCode,
        customerKey: c.customerKey,
        reason: res.reason || "",
        assignedTo: res.assignedTo || null,
        transferredOn: res.transferredOn || undefined,
      });
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      uiToast({ type: "success", title: "Transferred to Modem Recovery" });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Couldn't transfer",
        message: e?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  // Wrong transfer? Take the customer out of Modem Recovery and back onto the
  // normal customer / follow-up flow (not once handed over, or if Recharged).
  const canMoveToActive = (c) =>
    Boolean(c.modemCase?._id) &&
    !c.modemCase.handedOver &&
    c.modemCase.state !== "recharged";
  const moveToActive = async (c) => {
    const ok = await uiConfirm({
      title: `Move ${c.name || c.userId} back to active customers?`,
      message:
        "Removes this modem-recovery case (it won't count toward anyone's transfers or outcomes) and puts the customer back on the normal follow-up flow. Use this when they were transferred by mistake.",
      confirmText: "Move back",
    });
    if (!ok) return;
    try {
      await cancelModemTransferApi(c.modemCase._id, c.partnerCode || partnerCode);
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      uiToast({ type: "success", title: "Moved back to active customers" });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Couldn't move back",
        message: e?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  // Super-admin only. Deletes ONE partner's copy of the customer.
  const deleteCustomer = async ({ code, customerKey, name, userId, hasDuplicate }) => {
    const ok = await uiConfirm({
      title: `Delete ${name || userId} from ${code}?`,
      message: hasDuplicate
        ? "This customer id also exists under another partner code, so only this copy is deleted. Its follow-ups, status history, notes, modem-recovery cases and stored sheet rows for this partner go with it. This cannot be undone."
        : "WARNING: this is the only record of this customer id. Its follow-ups, status history, notes, modem-recovery cases and stored sheet rows go with it, and it comes back only if a later sheet upload contains it. This cannot be undone.",
      confirmText: "Delete permanently",
    });
    if (!ok) return;
    try {
      await deleteCustomerApi(code, customerKey);
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      uiToast({ type: "success", title: `Deleted ${name || userId} (${code})` });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Couldn't delete",
        message: e?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  // In "All partners" view the copies are already rows on the page, so only
  // show the extra amber rows for copies that are not on this page.
  const pageIds = new Set(rows.map((r) => String(r._id)));

  const toggleTransferSelect = (customerKey) =>
    setSelectedForTransfer((prev) => {
      const next = new Set(prev);
      if (next.has(customerKey)) next.delete(customerKey);
      else next.add(customerKey);
      return next;
    });

  // Bulk-transfer every checked Terminated customer into Modem Recovery in
  // one action — same endpoint as the Followups board's bulk transfer.
  const bulkTransferToRecovery = async () => {
    if (!selectedForTransfer.size) return;
    const res = await uiPrompt({
      title: `Transfer ${selectedForTransfer.size} customer(s) to Modem Recovery`,
      confirmText: "Transfer",
      fields: [
        {
          name: "assignedTo",
          label: "Assign to",
          type: "select",
          options: [
            { value: "", label: "Unassigned" },
            ...staff.map((s) => ({ value: s._id, label: s.name })),
          ],
        },
        {
          name: "reason",
          label: "Reason (optional)",
          type: "textarea",
          placeholder: "e.g. Terminated / customer leaving — recover the modem",
        },
        ...(isSuperAdmin
          ? [
              {
                name: "transferredOn",
                label: "Transferred on (back-date if needed)",
                type: "date",
                initial: fmtDateInput(),
                max: fmtDateInput(),
              },
            ]
          : []),
      ],
    });
    if (res === null) return;
    setBulkBusy(true);
    try {
      const r = await bulkTransferToModemRecoveryApi({
        partnerCode,
        customerKeys: [...selectedForTransfer],
        reason: res.reason || "",
        assignedTo: res.assignedTo || null,
        transferredOn: res.transferredOn || undefined,
      });
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      setSelectedForTransfer(new Set());
      uiToast({
        type: "success",
        title: `Transferred ${r?.data?.created ?? 0} to Modem Recovery`,
        message: r?.data?.skipped?.length
          ? `${r.data.skipped.length} skipped (already has a case).`
          : undefined,
      });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Couldn't transfer",
        message: e?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  // "All" view is returned status-grouped by the API (Inactive → Suspended →
  // Active → Terminated last). Insert a heading row each time the status
  // changes so the grouping is visible. Bucket tabs are single-status, so no
  // headings there.
  const showGroups = !bucket;
  const STATUS_LABEL = {
    Inactive: "Inactive",
    Suspended: "Suspended",
    Active: "Active",
    Terminated: "Terminated",
  };

  // Same wording/tone convention as the Followups board's parked-row label.
  // Employee name appended when the case still has one on file — cases with
  // no assignee (unassigned, or handed over and cleared) omit it.
  const modemCaseInfo = (c) => {
    const kase = c.modemCase;
    if (!kase) return null;
    const label = kase.handedOver
      ? "Handed over to the company"
      : kase.state === "recharged"
        ? "Recharged"
        : kase.state === "recovered"
          ? "Modem recovered"
          : kase.state === "untraceable"
            ? "Untraceable"
            : "Transferred to modem recovery";
    const tone =
      kase.handedOver || ["recovered", "recharged"].includes(kase.state)
        ? "text-emerald-600"
        : "text-rose-600";
    return { label, tone, employeeName: kase.assignedToName || "" };
  };

  const applySearch = (e) => {
    e.preventDefault();
    setQLive(q.trim());
    setPage(1);
  };

  return (
    <>
      <PageTitle activeMenu="Customers" motherMenu="Netplus" />

      <section className={panelClass}>
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          {CUSTOMER_BUCKETS.map((b) => (
            <button
              key={b.key || "all"}
              onClick={() => {
                setBucket(b.key);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                bucket === b.key
                  ? "bg-cyan-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {b.label}
            </button>
          ))}
          <form onSubmit={applySearch} className="ml-auto flex items-center gap-2">
            <label
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2 py-1"
              style={{ backgroundColor: "#fff" }}
              title="Partner code"
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Partner
              </span>
              <select
                className="bg-white text-xs font-semibold text-gray-800 outline-none"
                style={{ backgroundColor: "#fff", color: "#1f2937" }}
                value={viewAll ? "ALL" : partnerCode}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "ALL") {
                    setViewAll(true);
                  } else {
                    setViewAll(false);
                    setPartnerCode(v);
                  }
                  setPage(1);
                }}
              >
                <option value="ALL" style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                  All partners
                </option>
                {(allPartnerCodes.length ? allPartnerCodes : [partnerCode]).map((c) => (
                  <option key={c} value={c} style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <input
              type="date"
              className={fieldClass}
              title="Activation date from"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              className={fieldClass}
              title="Activation date to"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
            <input
              className={`${fieldClass} w-56`}
              placeholder="Name / phone / id"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button type="submit" className={btnGhost}>
              Search
            </button>
            <button
              type="button"
              className={btnGhost}
              onClick={() =>
                downloadWorkbook(partnerCode, { view: "customers", bucket })
              }
            >
              Export
            </button>
          </form>
        </div>

        {!viewAll && selectedForTransfer.size > 0 && (
          <div className="flex items-center gap-3 border-b border-cyan-100 bg-cyan-50/60 px-5 py-2 text-sm">
            <span className="font-semibold text-cyan-800">
              {selectedForTransfer.size} selected
            </span>
            <button
              className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={bulkTransferToRecovery}
              disabled={bulkBusy}
            >
              {bulkBusy
                ? "Transferring…"
                : `Transfer ${selectedForTransfer.size} → Modem Recovery`}
            </button>
            <button
              className="text-xs text-gray-500 hover:text-gray-700"
              onClick={() => setSelectedForTransfer(new Set())}
            >
              Clear
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                {viewAll ? (
                  <th className="px-4 py-3 text-left">Partner</th>
                ) : (
                  <th className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={allTerminatedSelected}
                      onChange={toggleSelectAllTerminated}
                      disabled={!terminatedKeys.length}
                      aria-label="Select all"
                      title="Select all Active and Terminated customers on this page"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">User Id</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Activation</th>
                <th className="px-4 py-3 text-center">Expiry</th>
                <th className="px-4 py-3 text-center">Overdue</th>
                <th className="px-4 py-3 text-center">Expiring in</th>
                <th className="px-4 py-3 text-right">Security</th>
                <th className="px-4 py-3 text-right">Modem</th>
                <th className="px-4 py-3 text-right">{isSuperAdmin ? "Delete" : ""}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-gray-400">
                    No customers.
                  </td>
                </tr>
              ) : (
                rows.map((c, i) => (
                  <React.Fragment key={c._id}>
                    {showGroups && rows[i - 1]?.status !== c.status && (
                      <tr className="bg-gray-50/80">
                        <td
                          colSpan={12}
                          className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                        >
                          {STATUS_LABEL[c.status] || c.status}
                        </td>
                      </tr>
                    )}
                  <tr
                    onClick={() =>
                      setSelected({ customerKey: c.customerKey, partnerCode: c.partnerCode })
                    }
                    className={`cursor-pointer hover:bg-cyan-50/40 ${
                      bucket === "duplicate" && rows[i - 1] && rows[i - 1].customerKey !== c.customerKey
                        ? "border-t-2 border-amber-300"
                        : ""
                    }`}
                  >
                    {viewAll ? (
                      <td className="px-4 py-2.5 text-xs font-semibold text-gray-500">
                        {c.partnerCode}
                      </td>
                    ) : (
                      <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                        {canRecoverModem(c) && (
                          <input
                            type="checkbox"
                            checked={selectedForTransfer.has(c.customerKey)}
                            onChange={() => toggleTransferSelect(c.customerKey)}
                            aria-label={`Select ${c.name || c.userId}`}
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-medium text-gray-800">
                      {c.name || "—"}
                      {c.duplicates?.length > 0 && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                          Duplicate id ×{c.duplicates.length + 1}
                        </span>
                      )}
                      {(() => {
                        const info = modemCaseInfo(c);
                        return (
                          info && (
                            <div className={`mt-0.5 text-xs font-medium ${info.tone}`}>
                              → {info.label}
                              {info.employeeName ? ` — ${info.employeeName}` : ""}
                            </div>
                          )
                        );
                      })()}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{c.userId}</td>
                    <td className="px-4 py-2.5 text-gray-500">{c.phone || "—"}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-500">
                      {fmtDate(c.activationDate)}
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-500">
                      {fmtDate(c.expiryDate)}
                    </td>
                    <td className="px-4 py-2.5 text-center tabular-nums">
                      {c.daysOverdue != null ? (
                        <span className="text-rose-600">{c.daysOverdue}d</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center tabular-nums">
                      {c.expiringInDays != null ? `${c.expiringInDays}d` : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                      {c.security != null
                        ? `₹${Number(c.security).toLocaleString("en-IN")}`
                        : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {canMoveToActive(c) && (
                        <button
                          className="mb-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveToActive(c);
                          }}
                        >
                          Move to active
                        </button>
                      )}
                      {canRecoverModem(c) && (
                        <div className="flex justify-end gap-2">
                          <button
                            className={btnGhost}
                            onClick={(e) => {
                              e.stopPropagation();
                              transferToRecovery(c);
                            }}
                          >
                            Transfer to Modem Recovery
                          </button>
                          <button
                            className={btnGhost}
                            onClick={(e) => {
                              e.stopPropagation();
                              recoverModem(c);
                            }}
                          >
                            Modem recovered
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      {isSuperAdmin && (
                        <button
                          className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                          onClick={() =>
                            deleteCustomer({
                              code: c.partnerCode,
                              customerKey: c.customerKey,
                              name: c.name,
                              userId: c.userId,
                              hasDuplicate: (c.duplicates || []).length > 0,
                            })
                          }
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                  {(c.duplicates || [])
                    .filter((d) => !(viewAll && pageIds.has(String(d._id))))
                    .map((d) => (
                      <tr key={`dup-${c._id}-${d._id}`} className="bg-amber-50/80">
                        <td className="px-4 py-2 text-xs font-semibold text-amber-700">
                          {d.partnerCode}
                        </td>
                        <td className="px-4 py-2 text-gray-700">
                          {d.name || "—"}
                          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                            Same id in {d.partnerCode}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">{d.userId}</td>
                        <td className="px-4 py-2 text-gray-500">{d.phone || "—"}</td>
                        <td className="px-4 py-2 text-center">
                          <Badge tone={STATUS_TONE[d.status]}>{d.status}</Badge>
                        </td>
                        <td className="px-4 py-2 text-center text-gray-500">{fmtDate(d.activationDate)}</td>
                        <td className="px-4 py-2 text-center text-gray-500">{fmtDate(d.expiryDate)}</td>
                        <td colSpan={4} />
                        <td className="px-4 py-2 text-right">
                          {isSuperAdmin && (
                            <button
                              className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                              onClick={() =>
                                deleteCustomer({
                                  code: d.partnerCode,
                                  customerKey: c.customerKey,
                                  name: d.name,
                                  userId: d.userId,
                                  hasDuplicate: true,
                                })
                              }
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{data?.totalCount ?? 0} total</span>
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      <CustomerDrawer
        partnerCode={selected?.partnerCode || partnerCode}
        customerKey={selected?.customerKey}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

export default NcsCustomers;
